import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { getDataCellExportValue } from "components/datatable/DataCell";
import React, { Fragment } from "react";
import PDFTableCell from "./PDFTableCell";
import PDFTableRow from "./PDFTableRow";
import { getStrDateLong, postgresDateToIndonesia } from "utils/MyUtils";

const PDFTable = (props) => {
  const { data, dlist, coltype, pdflist, title, border, filter, ...rest } =
    props;
  let columns = pdflist?.columns;
  columns = columns ? columns : dlist?.columns;
  const headerGroups = pdflist?.headerGroups || dlist?.headerGroups || [];
  const filtered = dlist?.filter;
  const groupedColumns = new Set((Array.isArray(headerGroups) ? headerGroups : []).flatMap((group) => group?.columns || []));
  const visibleColumns = (columns || []).filter((col) => col in coltype && col !== "action");
  const rawColumnWidths = visibleColumns.reduce((result, col) => {
    result[col] = col === "no" ? (Number(coltype?.[col]?.pdfwidth) || 5) : (Number(coltype?.[col]?.pdfwidth) || 0);
    return result;
  }, {});
  const totalExplicitWidth = Object.values(rawColumnWidths).reduce((total, item) => total + (Number(item) || 0), 0);
  const autoWidthColumns = visibleColumns.filter((col) => !rawColumnWidths[col]);
  const normalizedColumnWidths = visibleColumns.reduce((result, col) => {
    const rawWidth = Number(rawColumnWidths[col]) || 0;
    if (autoWidthColumns.length > 0) {
      result[col] = rawWidth || Math.max((100 - totalExplicitWidth) / autoWidthColumns.length, 0);
    } else if (totalExplicitWidth > 0) {
      result[col] = (rawWidth / totalExplicitWidth) * 100;
    } else {
      result[col] = 100 / Math.max(visibleColumns.length, 1);
    }
    return result;
  }, {});
  const columnWidth = (col) => {
    return normalizedColumnWidths[col] || 0;
  };
  const renderHeaderCell = (col, idx, textOverride) => {
    const val = textOverride || (col === "no" ? "No" : (coltype?.[col]?.headerLabel || coltype?.[col]?.label));
    const horizontal = coltype?.[col]?.align
      ? coltype[col].align
      : "left";
    return col in coltype && col !== "action" ? (
      <PDFTableCell
        key={col + idx}
        horizontal={horizontal}
        text={val}
        width={columnWidth(col)}
        backgroundColor="#CECECE"
      ></PDFTableCell>
    ) : (
      <Fragment key={col + idx}></Fragment>
    );
  };
  const headerStyles = StyleSheet.create({
    groupedHeaderRow: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      borderLeftWidth: border,
      borderTopWidth: border,
      borderStyle: "solid",
      borderColor: "#000000",
      marginTop: -1,
    },
    groupedHeaderCell: {
      backgroundColor: "#CECECE",
      borderRightWidth: border,
      borderBottomWidth: border,
      borderStyle: "solid",
      borderColor: "#000000",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 42,
      paddingHorizontal: 3,
      paddingVertical: 3,
    },
    groupedHeaderBlock: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#CECECE",
      borderRightWidth: border,
      borderBottomWidth: border,
      borderStyle: "solid",
      borderColor: "#000000",
      minHeight: 42,
    },
    groupedHeaderTitle: {
      minHeight: 21,
      borderBottomWidth: border,
      borderStyle: "solid",
      borderColor: "#000000",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 3,
      paddingVertical: 2,
    },
    groupedHeaderSubRow: {
      display: "flex",
      flexDirection: "row",
      minHeight: 21,
    },
    groupedHeaderSubCell: {
      alignItems: "center",
      justifyContent: "center",
      borderRightWidth: border,
      borderStyle: "solid",
      borderColor: "#000000",
      paddingHorizontal: 2,
      paddingVertical: 2,
    },
    groupedHeaderSubCellLast: {
      borderRightWidth: 0,
    },
    groupedHeaderText: {
      fontSize: 8,
      textAlign: "center",
    },
  });
  const renderGroupedHeader = () => (
    <View wrap={false} style={headerStyles.groupedHeaderRow}>
      {columns &&
        columns.map((col, idx) => {
          if (!(col in coltype) || col === "action") {
            return <Fragment key={col + idx}></Fragment>;
          }
          const group = headerGroups.find((item) => (item?.columns || [])[0] === col);
          if (group) {
            const visibleColumns = (group.columns || []).filter((item) => columns.includes(item) && item in coltype && item !== "action");
            const groupWidth = visibleColumns.reduce((total, item) => total + columnWidth(item), 0);
            return (
              <View key={group.label + idx} style={[headerStyles.groupedHeaderBlock, { width: `${groupWidth}%` }]}>
                <View style={headerStyles.groupedHeaderTitle}>
                  <Text style={headerStyles.groupedHeaderText}>{group.label}</Text>
                </View>
                <View style={headerStyles.groupedHeaderSubRow}>
                  {visibleColumns.map((item, subIdx) => (
                    <View
                      key={item}
                      style={[
                        headerStyles.groupedHeaderSubCell,
                        { width: `${(columnWidth(item) / groupWidth) * 100}%` },
                        subIdx === visibleColumns.length - 1 ? headerStyles.groupedHeaderSubCellLast : {},
                      ]}
                    >
                      <Text style={headerStyles.groupedHeaderText}>{coltype?.[item]?.headerLabel || coltype?.[item]?.label}</Text>
                    </View>
                  ))}
                </View>
              </View>
            );
          }
          if (groupedColumns.has(col)) {
            return <Fragment key={col + idx}></Fragment>;
          }
          const val = col === "no" ? "No" : coltype?.[col]?.label;
          return (
            <View key={col + idx} style={[headerStyles.groupedHeaderCell, { width: `${columnWidth(col)}%` }]}>
              <Text style={headerStyles.groupedHeaderText}>{val}</Text>
            </View>
          );
        })}
    </View>
  );

  let usedFilter = filtered
    ? filtered.map((field) => {
        const { dtype, name, label, options, valueId, labelId } = field;
        if (dtype && name) {
          switch (dtype) {
            case "dropdown":
              const itfs =
                options &&
                options.filter(
                  (itemop) =>
                    String(itemop[valueId]) === String(filter?.[name]),
                );
              if (itfs && itfs.length > 0) {
                const result = itfs[0][labelId];
                return (
                  <View key={name}>
                    <Text>{label + ": " + result + "\n"}</Text>
                  </View>
                );
              } else {
                return null;
              }
            case "q":
              if (filter?.q && filter.q.length > 0) {
                return (
                  <View key={name}>
                    <Text>{"Keywords: " + filter.q + "\n"}</Text>
                  </View>
                );
              } else {
                return null;
              }
            case "date":
              if (filter?.[name]) {
                const result = (
                  <View key={name}>
                    <Text>{label + ": " + (postgresDateToIndonesia(filter?.[name]) || getStrDateLong(filter?.[name]))}</Text>
                  </View>
                );
                return result;
              } else {
                return null;
              }
            default:
              return null;
          }
        } else {
          return null;
        }
      })
    : [];

  usedFilter = usedFilter.filter((item) => item != null);
  usedFilter =
    usedFilter.length > 0
      ? [
          ...usedFilter,
          <View key="kosong">
            <Text> </Text>
          </View>,
        ]
      : [];

  return (
    <Fragment>
      {title && (
        <View style={StyleSheet.create({ textAlign: "center", fontSize: 10 })}>
          <Text>
            {title}
            {"\n"}
            {"\n"}
          </Text>
        </View>
      )}
      {usedFilter}
      {headerGroups.length > 0 ? (
        renderGroupedHeader()
      ) : (
        <PDFTableRow width={100} border={border}>
          {columns && columns.map((col, idx) => renderHeaderCell(col, idx))}
        </PDFTableRow>
      )}
      {data &&
        data.map((item, i) => {
          return (
            <PDFTableRow key={i} width={100} border={border}>
              {columns &&
                columns.map((col, idx) => {
                  const val =
                    col === "no"
                      ? i + 1
                      : getDataCellExportValue(
                          item[col],
                          col,
                          item,
                          coltype[col],
                          "pdf",
                        );
                  const horizontal = coltype?.[col]?.align
                    ? coltype[col].align
                    : "left";
                  return col in coltype && col !== "action" ? (
                    <PDFTableCell
                      key={col + i + idx}
                      horizontal={horizontal}
                      text={val}
                      width={columnWidth(col)}
                      border={border}
                    ></PDFTableCell>
                  ) : (
                    <Fragment key={col + i + idx}></Fragment>
                  );
                })}
            </PDFTableRow>
          );
        })}
    </Fragment>
  );
};

PDFTable.defaultProps = {
  border: 1,
};

export default PDFTable;
